import { useState, useEffect } from 'react'
import { useNavigate } from 'react-router'
import { useAuthStore } from '@/stores/auth.store'
import { messagesApi } from '@/lib/messages-api'
import { trackEvent, Events } from '@/lib/observability'

interface SelectableParent {
  id: string
  name: string
  email: string
  childrenLabel?: string
  selected: boolean
}

export function NewConversationPage() {
  const navigate = useNavigate()
  const { user, accessToken } = useAuthStore()
  const [parents, setParents] = useState<SelectableParent[]>([])
  const [loading, setLoading] = useState(true)
  const [creating, setCreating] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Conversation options
  const [conversationType, setConversationType] = useState<'direct' | 'group' | 'broadcast'>('direct')
  const [groupName, setGroupName] = useState('')
  const [allowReplies, setAllowReplies] = useState(true)
  const [broadcastMessage, setBroadcastMessage] = useState('')

  // For parents - their childminder
  const [childminder, setChildminder] = useState<{ id: string; name: string } | null>(null)

  useEffect(() => {
    const fetchData = async () => {
      if (!user || !accessToken) return

      try {
        setLoading(true)

        // Use the unified contacts endpoint
        const response = await fetch(
          `${import.meta.env.VITE_API_URL || ''}/api/messages/contacts`,
          {
            headers: {
              Authorization: `Bearer ${accessToken}`,
            },
            credentials: 'include',
          }
        )

        if (!response.ok) throw new Error('Failed to fetch contacts')

        const data = await response.json()

        if (user.role === 'childminder') {
          setParents(
            data.contacts.map((p: { id: string; name: string; email: string; childrenLabel?: string }) => ({
              id: p.id,
              name: p.name,
              email: p.email,
              childrenLabel: p.childrenLabel,
              selected: false,
            }))
          )
        } else {
          // Parent - set their childminder (first contact)
          if (data.contacts.length > 0) {
            setChildminder(data.contacts[0])
          }
        }
      } catch (err) {
        console.error('Error fetching data:', err)
        setError('Failed to load contacts')
      } finally {
        setLoading(false)
      }
    }

    fetchData()
  }, [user, accessToken])

  const toggleParent = (id: string) => {
    setParents((prev) =>
      prev.map((p) => (p.id === id ? { ...p, selected: !p.selected } : p))
    )
  }

  const selectedParents = parents.filter((p) => p.selected)

  const handleCreate = async () => {
    if (!user) return

    try {
      setCreating(true)
      setError(null)

      if (user.role === 'parent') {
        // Parent starting a direct chat with their childminder
        if (!childminder) {
          setError('No childminder found')
          return
        }

        const response = await messagesApi.createConversation({
          type: 'direct',
          participantIds: [childminder.id],
        })

        trackEvent(Events.CONVERSATION_STARTED, { conversationType: 'direct', role: 'parent' })
        navigate(`/messages/${response.conversation.id}`)
      } else {
        // Childminder creating conversation
        if (conversationType === 'broadcast') {
          // Create broadcast to all parents
          const response = await messagesApi.sendBroadcast({
            content: broadcastMessage,
            allowReplies,
            name: groupName || undefined,
          })

          trackEvent(Events.BROADCAST_SENT, { allowReplies })
          navigate(`/messages/${response.conversation.id}`)
        } else {
          // Direct or group
          if (selectedParents.length === 0) {
            setError('Please select at least one parent')
            return
          }

          const response = await messagesApi.createConversation({
            type: conversationType,
            participantIds: selectedParents.map((p) => p.id),
            name: conversationType === 'group' ? groupName || undefined : undefined,
          })

          trackEvent(Events.CONVERSATION_STARTED, {
            conversationType,
            participantCount: selectedParents.length,
            role: 'childminder',
          })
          navigate(`/messages/${response.conversation.id}`)
        }
      }
    } catch (err) {
      console.error('Error creating conversation:', err)
      setError('Failed to create conversation')
    } finally {
      setCreating(false)
    }
  }

  if (!user) return null

  // Parent view - simple
  if (user.role === 'parent') {
    return (
      <div className="min-h-full bg-white">
        <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 py-3">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/messages')}
              className="p-1 -ml-1 text-gray-600"
            >
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
            </button>
            <h1 className="text-xl font-semibold text-gray-900">New Message</h1>
          </div>
        </div>

        <div className="p-4">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
            </div>
          ) : childminder ? (
            <div className="space-y-4">
              <p className="text-gray-600">
                Start a conversation with your childminder:
              </p>
              <div className="p-4 bg-gray-50 rounded-lg">
                <p className="font-medium text-gray-900 ph-no-capture">{childminder.name}</p>
              </div>
              {error && <p className="text-red-600 text-sm">{error}</p>}
              <button
                onClick={handleCreate}
                disabled={creating}
                className="w-full rounded-lg bg-primary-600 px-4 py-3 text-white font-medium hover:bg-primary-700 disabled:opacity-50"
              >
                {creating ? 'Creating...' : 'Start Conversation'}
              </button>
            </div>
          ) : (
            <p className="text-gray-500 text-center py-8">
              No childminder found. Please ensure you have accepted an invitation.
            </p>
          )}
        </div>
      </div>
    )
  }

  // Childminder view
  return (
    <div className="min-h-full bg-white">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-100 px-4 py-3">
        <div className="flex items-center gap-3">
          <button
            onClick={() => navigate('/messages')}
            className="p-1 -ml-1 text-gray-600"
          >
            <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
          </button>
          <h1 className="text-xl font-semibold text-gray-900">New Message</h1>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600" />
        </div>
      ) : (
        <div className="p-4 space-y-6">
          {/* Conversation type selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Message Type
            </label>
            <div className="grid grid-cols-3 gap-2">
              <button
                type="button"
                onClick={() => setConversationType('direct')}
                className={`px-3 py-2 rounded-lg text-sm font-medium border ${
                  conversationType === 'direct'
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'bg-white border-gray-300 text-gray-700'
                }`}
              >
                Direct
              </button>
              <button
                type="button"
                onClick={() => setConversationType('group')}
                className={`px-3 py-2 rounded-lg text-sm font-medium border ${
                  conversationType === 'group'
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'bg-white border-gray-300 text-gray-700'
                }`}
              >
                Group
              </button>
              <button
                type="button"
                onClick={() => setConversationType('broadcast')}
                className={`px-3 py-2 rounded-lg text-sm font-medium border ${
                  conversationType === 'broadcast'
                    ? 'bg-primary-50 border-primary-500 text-primary-700'
                    : 'bg-white border-gray-300 text-gray-700'
                }`}
              >
                Broadcast
              </button>
            </div>
          </div>

          {/* Group/Broadcast name */}
          {(conversationType === 'group' || conversationType === 'broadcast') && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                {conversationType === 'group' ? 'Group Name (optional)' : 'Broadcast Title (optional)'}
              </label>
              <input
                type="text"
                value={groupName}
                onChange={(e) => setGroupName(e.target.value)}
                placeholder={conversationType === 'group' ? 'e.g., Trip Planning' : 'e.g., Weekly Update'}
                className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
              />
            </div>
          )}

          {/* Broadcast options */}
          {conversationType === 'broadcast' && (
            <>
              <div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={allowReplies}
                    onChange={(e) => setAllowReplies(e.target.checked)}
                    className="rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                  />
                  <span className="text-sm text-gray-700">Allow parents to reply</span>
                </label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Message
                </label>
                <textarea
                  value={broadcastMessage}
                  onChange={(e) => setBroadcastMessage(e.target.value)}
                  placeholder="Write your broadcast message..."
                  rows={4}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500"
                />
              </div>
            </>
          )}

          {/* Parent selection (not for broadcast) */}
          {conversationType !== 'broadcast' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Select {conversationType === 'direct' ? 'Parent' : 'Parents'}
              </label>
              {parents.length === 0 ? (
                <p className="text-gray-500 text-center py-4">
                  No connected parents. Invite parents to get started.
                </p>
              ) : (
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {parents.map((parent) => (
                    <button
                      key={parent.id}
                      type="button"
                      onClick={() => {
                        if (conversationType === 'direct') {
                          // Single selection
                          setParents((prev) =>
                            prev.map((p) => ({
                              ...p,
                              selected: p.id === parent.id,
                            }))
                          )
                        } else {
                          toggleParent(parent.id)
                        }
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg border text-left ${
                        parent.selected
                          ? 'bg-primary-50 border-primary-500'
                          : 'bg-white border-gray-200'
                      }`}
                    >
                      <div
                        className={`w-5 h-5 rounded-full border-2 flex items-center justify-center ${
                          parent.selected
                            ? 'bg-primary-600 border-primary-600'
                            : 'border-gray-300'
                        }`}
                      >
                        {parent.selected && (
                          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 20 20">
                            <path
                              fillRule="evenodd"
                              d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                              clipRule="evenodd"
                            />
                          </svg>
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-gray-900 ph-no-capture">
                          {parent.name}
                          {parent.childrenLabel && (
                            <span className="text-gray-500 font-normal"> ({parent.childrenLabel})</span>
                          )}
                        </p>
                        <p className="text-sm text-gray-500 ph-no-capture">{parent.email}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {error && <p className="text-red-600 text-sm">{error}</p>}

          {/* Create button */}
          <button
            onClick={handleCreate}
            disabled={
              creating ||
              (conversationType === 'broadcast' && !broadcastMessage.trim()) ||
              (conversationType !== 'broadcast' && selectedParents.length === 0)
            }
            className="w-full rounded-lg bg-primary-600 px-4 py-3 text-white font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {creating
              ? 'Creating...'
              : conversationType === 'broadcast'
                ? 'Send Broadcast'
                : 'Start Conversation'}
          </button>
        </div>
      )}
    </div>
  )
}
